<template>
  <div v-if="hasWorklist" class="bg-white border border-gray-200 rounded-lg">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <h3 class="text-sm font-semibold text-gray-800">
          {{ worklistName }}
          <span v-if="rows.length" class="text-gray-500 font-normal">({{ rows.length }}{{ hasMore ? '+' : '' }})</span>
        </h3>
        <button
          v-if="!loaded"
          @click="fetchData"
          :disabled="loading"
          class="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100 transition-colors"
        >
          {{ loading ? 'Loading...' : 'View Patients' }}
        </button>
      </div>
      <div class="flex items-center gap-2">
        <ExportMenu :disabled="!loaded || !rows.length" :exports="[{ type: 'pdf', action: exportPdf }, { type: 'excel', action: exportCsv, loading: exporting }]" />
      </div>
    </div>

    <!-- Column Controls -->
    <div v-if="showColumnBar && loaded && allColumns.length > 0" class="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Columns</span>
      <span
        v-for="col in activeColumnDefs"
        :key="col.field"
        class="inline-flex items-center gap-1 bg-neutral-50 border border-neutral-200 text-neutral-800 px-2.5 py-0.5 rounded text-xs font-medium"
      >
        {{ col.header }}
        <button
          @click="removeColumn(col.field)"
          class="text-neutral-400 hover:text-neutral-700 ml-0.5 leading-none transition-colors"
        >&times;</button>
      </span>
      <select
        v-if="hiddenColumns.length > 0"
        class="text-xs text-gray-500 bg-transparent border-0 py-0.5 pl-1 pr-4 cursor-pointer focus:outline-none"
        @change="addColumn($event.target.value); $event.target.value = ''"
      >
        <option value="">+ Add</option>
        <option v-for="col in hiddenColumns" :key="col.field" :value="col.field">
          {{ col.header }}{{ col.grain !== 'patient' ? ' (detail)' : '' }}
        </option>
      </select>
    </div>

    <!-- Filter Controls -->
    <div v-if="loaded" class="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Filters</span>

      <!-- Active filter chips -->
      <span
        v-for="chip in activeFilterChips"
        :key="chip.field"
        class="inline-flex items-center gap-1 bg-primary-50 border border-primary-200 text-primary-800 px-2.5 py-0.5 rounded text-xs font-medium"
      >
        <span>{{ chip.label }}: <span :class="{ sensitive: isSensitiveField(chip.field) }">{{ chip.display }}</span></span>
        <button
          @click="clearFilter(chip.field)"
          class="text-primary-400 hover:text-primary-700 ml-0.5 leading-none transition-colors"
        >&times;</button>
      </span>

      <!-- Add filter: pick column -->
      <select
        v-if="!pendingFilterField"
        class="text-xs text-gray-500 bg-transparent border-0 py-0.5 pl-1 pr-4 cursor-pointer focus:outline-none"
        @change="pendingFilterField = $event.target.value; $event.target.value = ''"
      >
        <option value="">+ Filter</option>
        <option
          v-for="col in filterableColumns"
          :key="col.field"
          :value="col.field"
        >{{ col.header }}</option>
      </select>

      <!-- Value selector for chosen column -->
      <span v-if="pendingFilterField" class="inline-flex items-center gap-1.5">
        <span class="text-xs text-gray-600 font-medium">{{ pendingFilterLabel }}:</span>

        <!-- String: SimpleMultiSelect -->
        <SimpleMultiSelect
          v-if="pendingFilterType === 'string'"
          :model-value="filterModel[pendingFilterField]?.values || []"
          :options="(filterOptions[pendingFilterField] || []).map(v => ({ value: v, label: String(v ?? '(blank)') }))"
          placeholder="Select..."
          :searchable="true"
          :sensitive-labels="isSensitiveField(pendingFilterField)"
          @update:model-value="setInFilter(pendingFilterField, $event)"
        />

        <!-- Boolean -->
        <select
          v-else-if="pendingFilterType === 'boolean'"
          :value="filterModel[pendingFilterField]?.value ?? ''"
          @change="setBoolFilter(pendingFilterField, $event.target.value); pendingFilterField = null"
          class="text-xs px-2 py-1 border border-gray-200 rounded bg-white text-gray-700 focus:outline-none"
        >
          <option value="" disabled>Select…</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>

        <!-- Integer / Float -->
        <template v-else-if="pendingFilterType === 'integer' || pendingFilterType === 'float'">
          <input
            type="number"
            :value="filterModel[pendingFilterField]?.min ?? ''"
            @input="setRangeFilter(pendingFilterField, 'min', $event.target.value)"
            placeholder="Min"
            class="text-xs px-2 py-1 border border-gray-200 rounded bg-white text-gray-700 w-20 focus:outline-none"
          />
          <span class="text-xs text-gray-400">–</span>
          <input
            type="number"
            :value="filterModel[pendingFilterField]?.max ?? ''"
            @input="setRangeFilter(pendingFilterField, 'max', $event.target.value)"
            placeholder="Max"
            class="text-xs px-2 py-1 border border-gray-200 rounded bg-white text-gray-700 w-20 focus:outline-none"
          />
        </template>

        <!-- Date -->
        <template v-else-if="pendingFilterType === 'date'">
          <input
            type="date"
            :value="filterModel[pendingFilterField]?.min ?? ''"
            @input="setRangeFilter(pendingFilterField, 'min', $event.target.value)"
            class="text-xs px-2 py-1 border border-gray-200 rounded bg-white text-gray-700 focus:outline-none"
          />
          <span class="text-xs text-gray-400">–</span>
          <input
            type="date"
            :value="filterModel[pendingFilterField]?.max ?? ''"
            @input="setRangeFilter(pendingFilterField, 'max', $event.target.value)"
            class="text-xs px-2 py-1 border border-gray-200 rounded bg-white text-gray-700 focus:outline-none"
          />
        </template>

        <template v-if="pendingFilterType === 'string'">
          <button
            @click="commitPendingFilter"
            class="px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          >Apply</button>
          <button
            @click="cancelPendingFilter"
            class="px-2 py-0.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >Cancel</button>
        </template>
        <button
          v-else
          @click="commitPendingFilter"
          class="text-gray-400 hover:text-gray-600 text-xs leading-none transition-colors"
        >&times;</button>
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading && !rows.length" class="flex items-center justify-center py-8">
      <LoadingIndicator />
    </div>

    <!-- Error -->
    <div v-if="error" class="px-4 py-3 mx-4 my-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
      Unable to load patient data for this metric.
      <button class="ml-2 underline hover:text-red-900" @click="fetchData">Retry</button>
    </div>

    <!-- Table -->
    <div v-else-if="loaded && rows.length > 0" class="overflow-x-auto max-h-[700px] overflow-y-auto">
      <table class="w-full text-xs">
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th
              v-for="col in activeColumnDefs"
              :key="col.field"
              :class="['px-3 py-2 font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:text-gray-900',
                        col.type === 'integer' || col.type === 'float' ? 'text-right' : 'text-left']"
              @click="toggleSort(col.field)"
            >
              {{ col.header }}
              <span v-if="sortKey === col.field" class="ml-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, i) in rows"
            :key="i"
            :class="['border-t border-gray-100 cursor-pointer', row.recaptured_date ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50']"
            @click="selectPatient(row)"
          >
            <td
              v-for="col in activeColumnDefs"
              :key="col.field"
              :class="['px-3 py-2', col.type === 'integer' || col.type === 'float' ? 'text-right font-mono' : '', isSensitiveField(col.field) ? 'sensitive' : '']"
            >{{ formatValue(row[col.field], col.type, col.field) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="hasMore" class="px-4 py-3 border-t border-gray-200 flex justify-center">
        <button
          @click="loadMore"
          :disabled="loadingMore"
          class="px-4 py-2 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100 transition-colors"
        >
          {{ loadingMore ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="loaded" class="flex items-center justify-center py-8 text-gray-400 text-sm">
      No patient data available
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import { generatePdf } from '@/utils/pdfExport';
import { beginSensitiveEgress } from '@/utils/demoEgress';

const PAGE_SIZE = 2000;
const FILTER_DEBOUNCE_MS = 400;

export default {
  name: 'PatientWorklist',
  components: { ExportMenu, LoadingIndicator, SimpleMultiSelect },
  props: {
    metricIds: { type: Array, default: () => [] },
    worklistIdProp: { type: [Number, String], default: null },
    entityId: { type: [Number, String], default: null },
    observationYear: { type: Number, default: null },
    year: { type: Number, default: null },
    autoLoad: { type: Boolean, default: null },
    showColumnBar: { type: Boolean, default: true },
  },
  emits: ['select-patient'],
  data() {
    return {
      worklistId: null,
      worklistName: 'Patient Worklist',
      hasWorklist: false,
      allColumns: [],
      availableColumns: [],
      selectedFields: [],
      rows: [],
      loading: false,
      loadingMore: false,
      exporting: false,
      loaded: false,
      error: null,
      hasMore: false,
      sortKey: null,
      sortDir: 'asc',
      filterModel: {},
      filterOptions: {},
      pendingFilterField: null,
      filterDebounceTimer: null,
    };
  },
  computed: {
    shouldAutoLoad() {
      if (this.autoLoad !== null) return this.autoLoad;
      return !!this.worklistIdProp;
    },
    effectiveYear() {
      return this.observationYear || this.year || 2025;
    },
    effectiveMetricId() {
      return this.metricIds.length ? Number(this.metricIds[0]) : null;
    },
    effectiveEntityId() {
      return this.entityId ? Number(this.entityId) : null;
    },
    activeColumnDefs() {
      const fieldSet = new Set(this.selectedFields);
      return this.allColumns
        .filter(c => fieldSet.has(c.field))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    hiddenColumns() {
      const fieldSet = new Set(this.selectedFields);
      return this.availableColumns
        .filter(c => !fieldSet.has(c.field))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    // Columns available to filter on — string cols need options loaded; others always available
    filterableColumns() {
      return this.activeColumnDefs.filter(col => {
        if (col.type === 'string') return (this.filterOptions[col.field]?.length || 0) > 0;
        return ['boolean', 'integer', 'float', 'date'].includes(col.type);
      });
    },
    pendingFilterType() {
      if (!this.pendingFilterField) return null;
      return this.activeColumnDefs.find(c => c.field === this.pendingFilterField)?.type || 'string';
    },
    pendingFilterLabel() {
      return this.activeColumnDefs.find(c => c.field === this.pendingFilterField)?.header || this.pendingFilterField;
    },
    activeFilterChips() {
      const chips = [];
      for (const [field, spec] of Object.entries(this.filterModel)) {
        if (!spec) continue;
        const col = this.activeColumnDefs.find(c => c.field === field);
        const label = col?.header || field;
        let display = '';
        if (spec.type === 'in' && spec.values?.length) {
          display = spec.values.length === 1 ? String(spec.values[0]) : `${spec.values.length} selected`;
        } else if (spec.type === 'boolean') {
          display = spec.value ? 'Yes' : 'No';
        } else if (spec.type === 'range') {
          if (spec.min != null && spec.max != null) display = `${spec.min} – ${spec.max}`;
          else if (spec.min != null) display = `≥ ${spec.min}`;
          else if (spec.max != null) display = `≤ ${spec.max}`;
        }
        if (display) chips.push({ field, label, display });
      }
      return chips;
    },
    activeFilterModel() {
      const out = {};
      for (const [field, spec] of Object.entries(this.filterModel)) {
        if (!spec) continue;
        if (spec.type === 'in' && spec.values?.length) out[field] = spec;
        else if (spec.type === 'boolean' && spec.value !== null && spec.value !== undefined) out[field] = spec;
        else if (spec.type === 'range' && (spec.min !== null || spec.max !== null)) out[field] = spec;
      }
      return Object.keys(out).length ? out : null;
    },
  },
  async mounted() {
    await this.loadWorklistConfig();
  },
  watch: {
    effectiveMetricId() { this.reset(); this.loadWorklistConfig(); },
    worklistIdProp() { this.reset(); this.loadWorklistConfig(); },
    effectiveEntityId() { this.reset(); },
    effectiveYear() { this.reset(); },
  },
  methods: {
    isSensitiveField(field) {
      const f = (field || '').toLowerCase();
      return /^(bene_|patient_)/.test(f)
        || /_(name|phone|address|mbi|identifier|npi|dob|email|fax)/.test(f)
        || /^(practice_|provider_|facility_|payer_|contract_|entity_)name/.test(f)
        || /^(mbi|pcp_name|tin_name)$/.test(f);
    },
    reset() {
      this.loaded = false;
      this.rows = [];
      this.hasMore = false;
      this.sortKey = null;
      this.sortDir = 'asc';
      this.filterModel = {};
      this.filterOptions = {};
      this.pendingFilterField = null;
      this.error = null;
    },

    async loadWorklistConfig() {
      if (!this.effectiveMetricId && !this.worklistIdProp) {
        this.hasWorklist = false;
        return;
      }
      try {
        let worklistData;
        if (this.worklistIdProp) {
          const res = await axios.get(`/api/impact_analytics/patient-worklists/${this.worklistIdProp}/`);
          this.worklistId = Number(this.worklistIdProp);
          worklistData = {
            worklist_name: res.data.name,
            worklist_config: res.data.worklist_config,
          };
        } else {
          const res = await axios.post('/api/impact_analytics/patient-worklists/by-metric/', {
            metric_id: this.effectiveMetricId,
            observation_year: this.effectiveYear,
            start_row: 0,
            end_row: 0,
          });
          if (!res.data.worklist_id) {
            this.hasWorklist = false;
            return;
          }
          this.worklistId = res.data.worklist_id;
          worklistData = res.data;
        }
        const catalogRes = await axios.get('/api/impact_analytics/patient-worklists/available-columns/');
        this.worklistName = worklistData.worklist_name || 'Patient Worklist';
        this.allColumns = (worklistData.worklist_config?.columns || worklistData.columns || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        this.availableColumns = (catalogRes.data?.columns || []);
        this.selectedFields = this.allColumns.map(c => c.field);
        const defaultFilters = worklistData.worklist_config?.default_filters;
        if (defaultFilters && !Object.keys(this.filterModel).length) {
          this.filterModel = { ...defaultFilters };
        }
        const defaultSort = worklistData.worklist_config?.default_sort;
        if (defaultSort && !this.sortKey) {
          this.sortKey = defaultSort.field;
          this.sortDir = defaultSort.direction || 'desc';
        }
        this.hasWorklist = true;
        if (this.shouldAutoLoad) {
          this.fetchData();
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.hasWorklist = false;
        } else {
          this.hasWorklist = true;
          this.error = 'Failed to load worklist configuration';
          console.error('Worklist config error:', error.response?.data?.error || error.message);
        }
      }
    },

    activeColumnConfigs() {
      const fieldSet = new Set(this.selectedFields);
      return this.allColumns.filter(c => fieldSet.has(c.field));
    },

    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(`/api/impact_analytics/patient-worklists/${this.worklistId}/run/`, {
          selected_columns: this.selectedFields,
          column_configs: this.activeColumnConfigs(),
          entity_id: this.effectiveEntityId,
          observation_year: this.effectiveYear,
          filter_model: this.activeFilterModel,
          sort_model: this.sortKey ? [{ colId: this.sortKey, sort: this.sortDir }] : null,
          start_row: 0,
          end_row: PAGE_SIZE,
        });
        this.rows = response.data.rowData || [];
        this.hasMore = this.rows.length >= PAGE_SIZE;
        this.loaded = true;
        // Fetch filter options async — don't block data display
        this.fetchFilterOptions();
      } catch (error) {
        this.error = 'Unable to load patient data';
        console.error('Worklist query error:', error.response?.data?.error || error.message);
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },

    async fetchFilterOptions() {
      try {
        const response = await axios.post(`/api/impact_analytics/patient-worklists/${this.worklistId}/filters/`, {
          selected_columns: this.selectedFields,
          entity_id: this.effectiveEntityId,
          observation_year: this.effectiveYear,
        });
        if (response.data.filters) {
          this.filterOptions = response.data.filters;
        }
      } catch (error) {
        // Filter options are non-critical — degrade to text filters
        console.warn('Failed to load filter options:', error);
      }
    },

    async loadMore() {
      this.loadingMore = true;
      try {
        const response = await axios.post(`/api/impact_analytics/patient-worklists/${this.worklistId}/run/`, {
          selected_columns: this.selectedFields,
          entity_id: this.effectiveEntityId,
          observation_year: this.effectiveYear,
          filter_model: this.activeFilterModel,
          sort_model: this.sortKey ? [{ colId: this.sortKey, sort: this.sortDir }] : null,
          start_row: this.rows.length,
          end_row: this.rows.length + PAGE_SIZE,
        });
        const newRows = response.data.rowData || [];
        this.rows = this.rows.concat(newRows);
        this.hasMore = newRows.length >= PAGE_SIZE;
      } catch (error) {
        console.error('Error loading more:', error);
      } finally {
        this.loadingMore = false;
      }
    },

    addColumn(field) {
      if (!field || this.selectedFields.includes(field)) return;
      if (!this.allColumns.find(c => c.field === field)) {
        const catalogCol = this.availableColumns.find(c => c.field === field);
        if (catalogCol) this.allColumns.push(catalogCol);
      }
      this.selectedFields.push(field);
      if (this.loaded) {
        this.reset();
        this.fetchData();
      }
    },

    removeColumn(field) {
      if (this.selectedFields.length <= 1) return;
      this.selectedFields = this.selectedFields.filter(f => f !== field);
      this.clearFilter(field);
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
      if (this.hasMore) {
        this.rows = [];
        this.hasMore = false;
        this.fetchData();
      } else {
        this.sortRowsLocally();
      }
    },

    sortRowsLocally() {
      const key = this.sortKey;
      if (!key) return;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const col = this.activeColumnDefs.find(c => c.field === key);
      const numeric = col && (col.type === 'integer' || col.type === 'float');
      this.rows.sort((a, b) => {
        let va = a[key], vb = b[key];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (numeric) return (Number(va) - Number(vb)) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    },

    // ── Filter setters ────────────────────────────────────────────────────────

    setInFilter(field, values) {
      if (!values.length) {
        const m = { ...this.filterModel };
        delete m[field];
        this.filterModel = m;
      } else {
        this.filterModel = { ...this.filterModel, [field]: { type: 'in', values } };
      }
    },

    setBoolFilter(field, value) {
      if (value === '') {
        this.clearFilter(field);
      } else {
        this.filterModel = { ...this.filterModel, [field]: { type: 'boolean', value: value === 'true' } };
      }
      this.scheduleFilterFetch();
    },

    setRangeFilter(field, bound, value) {
      const current = this.filterModel[field] || { type: 'range', min: null, max: null };
      this.filterModel = {
        ...this.filterModel,
        [field]: { ...current, type: 'range', [bound]: value === '' ? null : value },
      };
      this.scheduleFilterFetch();
    },

    commitPendingFilter() {
      this.pendingFilterField = null;
      this.scheduleFilterFetch();
    },

    cancelPendingFilter() {
      // Revert any in-progress string selection by clearing the pending field's filter
      if (this.pendingFilterField) {
        const m = { ...this.filterModel };
        delete m[this.pendingFilterField];
        this.filterModel = m;
      }
      this.pendingFilterField = null;
    },

    clearFilter(field) {
      const m = { ...this.filterModel };
      delete m[field];
      this.filterModel = m;
      if (this.pendingFilterField === field) this.pendingFilterField = null;
      this.scheduleFilterFetch();
    },

    scheduleFilterFetch() {
      if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(() => {
        if (!this.loaded) return;
        this.rows = [];
        this.hasMore = false;
        this.fetchData();
      }, FILTER_DEBOUNCE_MS);
    },

    // ─────────────────────────────────────────────────────────────────────────

    formatValue(val, type, field) {
      if (val === null || val === undefined) return '—';
      if (type === 'boolean')
        return val === true || val === 'true' || val === 'True' ? 'Yes' : 'No';
      if (type === 'date') return val ? String(val).split('T')[0] : '—';
      if (type === 'float') return Number(val).toFixed(2);
      if (type === 'integer') return Number(val).toLocaleString();
      if (field && /phone|fax/i.test(field)) {
        const digits = String(val).replace(/\D/g, '');
        if (digits.length === 10)
          return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        if (digits.length === 11 && digits[0] === '1')
          return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        return val;
      }
      return val;
    },

    selectPatient(row) {
      this.$emit('select-patient', { bene_mbi_id: row.bene_mbi_id, row });
    },

    exportPdf() {
      const cols = this.activeColumnDefs;
      const columns = cols.map(col => ({
        header: col.header,
        dataKey: col.field,
      }));
      const typeMap = Object.fromEntries(cols.map(c => [c.field, c.type]));
      const rowData = this.rows.map(row => {
        const out = {};
        for (const col of cols) {
          out[col.field] = this.formatValue(
            row[col.field],
            typeMap[col.field],
            col.field,
          );
        }
        return out;
      });
      generatePdf({
        rowData,
        columns,
        title: `${this.worklistName} – ${this.effectiveYear}`,
        filename: `${this.worklistName.replace(/\s+/g, '_')}_${this.effectiveYear}.pdf`,
        orientation: 'l',
      });
    },

    async exportCsv() {
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      this.exporting = true;
      try {
        const response = await axios.post(
          `/api/impact_analytics/patient-worklists/${this.worklistId}/export/`,
          {
            selected_columns: this.selectedFields,
            entity_id: this.effectiveEntityId,
            observation_year: this.effectiveYear,
            filter_model: this.activeFilterModel,
          },
          { responseType: 'blob' },
        );
        if (!ticket.confirm()) return;
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.worklistName.replace(/\s+/g, '_')}_${this.effectiveYear}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting:', error);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
